import { useCallback } from "react";
import { useController } from "react-hook-form";

import { Option } from "@/types/common";
import { StyledSelect } from "./select.styles";
import { BaseFormElementProps } from "../types";

type SelectProps = BaseFormElementProps & {
    elementOptions?: Option[];
    options?: Option[];
    pale?: boolean;
    rounded?: boolean;
    color?: string;
    value?: Option | string | number;
    isMulti?: boolean;
    valueAndLabel?: boolean;
    onChange?: Function;
    spaceLeft?: string | number | boolean;
    spaceTop?: string | number | boolean;
    spaceBottom?: string | number | boolean;
    width?: string | number;
    [key: string]: any;
};

const getSelectValue = (options: Option[], value: number | string | Record<any, any>) => {
    if (Array.isArray(value)) {
        return value.map((currentValue) => getSelectValue(options, currentValue));
    }
    if (typeof value === "number" && !value) return "";
    if (typeof value === "object") {
        return value;
    }
    if (typeof value === "string") {
        return (
            options.find((opt) => opt.value === value) ??
            options.find((opt) => opt.label === value)
        );
    }
};

export function Select(props: SelectProps) {
    const {
        name,
        label,
        rules = {},
        elementOptions = [],
        options = [],
        valueAndLabel,
        defaultValue,
        error,
        isMulti,
        spaceLeft,
        spaceTop,
        spaceBottom,
        width,
        ...restProps
    } = props;

    const { field, fieldState } = useController({ name, defaultValue, rules });

    const handleChange = useCallback(
        (selected: Option | Option[]) => {
            if (!selected) return null;
            if (valueAndLabel) return field.onChange(selected);
            if (isMulti) {
                const selectedValues = (selected as Option[]).map(({ value }) => value);
                return field.onChange(selectedValues);
            }
            if (!Array.isArray(selected)) field.onChange(selected.value ?? selected.label);
        },
        [isMulti, field, valueAndLabel]
    );

    const selectOptions = elementOptions.length > 0 ? elementOptions : options;

    return (
        <StyledSelect
            name={name}
            label={label}
            value={getSelectValue(selectOptions, field.value)}
            onChange={handleChange}
            error={error || fieldState.error?.message}
            options={selectOptions}
            spaceLeft={spaceLeft}
            spaceTop={spaceTop}
            spaceBottom={spaceBottom}
            width={width}
            formatOptionLabel={(option: any) => (
                <div style={{ display: "flex", flexDirection: "column" }}>
                    <span>{option.label}</span>
                    {option.subText && (
                        <small style={{ fontSize: "0.8em", color: "#777" }}>
                            {option.subText}
                        </small>
                    )}
                </div>
            )}
            {...restProps}
        />
    );
}
